import { useEffect, useState } from "react";
import { ModalComponent } from "../../..";
import { parseStringToNumber } from "../../../../../helperFunctions";
import {
  AddLoanProductPayload,
  LoanProduct,
} from "../../../../../redux/features/loans/loans.type";
import { BackButton } from "../../../../buttons/backButton";
import { ProgressTrail } from "../../../../progress/progressTrail";
import { EmptyState } from "../../../../states/emptyState";
import { LoanInformation } from "./information";
import { MoreConfiguration } from "./moreConfiguration";

export const AddLoanProductModal = ({
  modalOpen,
  onModalClose = () => {},
  mode = "create",
  selectedProduct,
  refetch = () => {},
}: {
  modalOpen: boolean;
  onModalClose: () => void;
  mode: "create" | "edit";
  selectedProduct?: LoanProduct;
  refetch: () => void;
}) => {
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);
  const [finalPayload, setFinalPayload] = useState<AddLoanProductPayload>();

  const steps = ["Loan Information", "More Configuration"];

  const handleClose = () => {
    setFinalPayload(undefined);
    setShowSuccess(false);
    setStep(1);
    onModalClose();
  };

  useEffect(() => {
    if (modalOpen) {
      setFinalPayload({
        loanName: selectedProduct?.LoanName ?? "",
        loanDescription: selectedProduct?.LoanDescription ?? "",
        maxGuarantor: selectedProduct?.MaxGuarantor ?? 0,
        minMonthJoined: selectedProduct?.MinMonthJoined ?? 6,
        maxTenure: selectedProduct?.MaxTenure ?? 6,
        minLoanAmount: parseStringToNumber(selectedProduct?.MinAmount) ?? 0,
        maxXAmount: selectedProduct?.XAmount ?? 1,
        maxInterestRate:
          parseStringToNumber(selectedProduct?.MaxInterestRate) ?? 0,
        penaltyFee: parseStringToNumber(selectedProduct?.PenaltyFee) ?? 0,
        loanApplicationFee:
          parseStringToNumber(selectedProduct?.LoanApplicationFee) ?? 0,
        multipleRunningLoan: selectedProduct?.MultipleRunningLoan ?? false,
        EnableDisbursement: selectedProduct?.EnableDisbursement ?? true,
        paymentMode: selectedProduct?.payment_modes ?? [],
      });
    }
  }, [modalOpen, selectedProduct]);

  return (
    <ModalComponent
      isModalOpen={modalOpen}
      onModalClose={() => {
        handleClose();
      }}
      topTitle
      title={mode === "edit" ? "Edit Loan Product" : "Add Loan Product"}
      subTitle=""
      modalClassName="!max-w-[424px]"
      childrenClassName={`!min-h-fit`}
      proceedButtonText={
        step === 2 && mode === "create" ? "Add a loan product" : "Proceed"
      }
      hasButtons={false}
      isProceedDisabled={false}
      isLoading={false}
      onProceedClick={() => {}}
      hasCancelButton={false}
      cancelButtonText="Cancel"
      cancelButtonVariant="secondary"
      onCancelClick={() => {}}
      modalStyle="side"
    >
      {showSuccess ? (
        <EmptyState
          image={<img src={"/assets/images/success.svg"} alt="success" />}
          titleClassName="max-w-[267px]"
          title={
            mode === "create"
              ? `You’ve successfully added a loan product.`
              : `The loan product has been successfully updated.`
          }
          actionText={"Back to loan settings"}
          actionButtonVariant="primary"
          onClick={() => {
            handleClose();
          }}
          className="mt-[100px]"
        />
      ) : (
        <div className="flex flex-col gap-6">
          {!showSuccess && step > 1 && (
            <BackButton
              text={"Previous"}
              onClick={() => step !== 1 && setStep(step - 1)}
              className="!gap-1"
              backIcon="back-caret"
            />
          )}

          <ProgressTrail steps={steps} currentStep={step} />

          {step === 1 && finalPayload ? (
            <LoanInformation
              finalPayload={finalPayload}
              setFinalPayload={setFinalPayload}
              onSuccess={() => {
                setStep(step + 1);
              }}
              mode={mode}
            />
          ) : undefined}

          {step === 2 && finalPayload ? (
            <MoreConfiguration
              finalPayload={finalPayload}
              onSuccess={() => {
                setShowSuccess(true);
                refetch();
              }}
              mode={mode}
              selectedProduct={selectedProduct}
            />
          ) : undefined}
        </div>
      )}
    </ModalComponent>
  );
};
